import React from "react";
import {Stack, Box, Flex, useDisclosure} from "@chakra-ui/core";
import {useRouter, NextRouter} from "next/router";
import Content from "@pency/components/structure/Content";
import Well from "@pency/components/feedback/Well";

import ProductCard from "../../components/ProductCard";
import {useFilteredProducts} from "../../hooks";
import ProductsCarousel from "../../components/ProductsCarousel";

import Onboarding from "./Onboarding";

import {useCart} from "~/cart/hooks";
import {groupBy} from "~/selectors/group";
import CartSummaryDrawer from "~/cart/components/CartSummaryDrawer";
import {filterBy} from "~/selectors/filter";
import {useTenant} from "~/tenant/hooks";
import {useTranslation} from "~/i18n/hooks";
import TenantHeader from "~/tenant/components/TenantHeader";
import SummaryButton from "~/cart/components/SummaryButton";
import CartItemDrawer from "~/cart/components/CartItemDrawer";
import {Product} from "~/product/types";
import CategoriesList from "~/product/components/CategoriesList";

interface Router extends NextRouter {
  query: {
    product?: string;
  };
}

const ProductsScreen: React.FC = () => {
  const {
    query: {product},
    push,
  } = useRouter() as Router;
  const {add, increase, decrease, items, checkout} = useCart();
  const t = useTranslation();
  const {isOpen: isCartOpen, onOpen: openCart, onClose: closeCart} = useDisclosure();
  const {products, element, ...filters} = useFilteredProducts(
    (product) => product.type !== "hidden",
  );
  const {highlight, layout, flow, ...tenant} = useTenant();
  const selected = React.useMemo(
    () => (product ? products.find((_product) => _product.id === product) : null),
    [products, product],
  );
  const featuredProducts = React.useMemo(() => filterBy(products, {featured: true}), [products]);
  const productsByCategory = React.useMemo(() => groupBy(products, (product) => product.category), [
    products,
  ]);

  function changeRoute(params = {}) {
    push(
      {
        pathname: `/[id]`,
        query: params,
      },
      {
        pathname: `/${tenant.id}`,
        query: params,
      },
      {shallow: true},
    );
  }

  function handleAdd(product: Product, count: number, note: string) {
    add(product, count, note);

    changeRoute();
  }

  function handleOpenCart() {
    openCart();
  }

  function handleCloseSelected() {
    changeRoute();
  }

  function handleSelect(product: Product) {
    changeRoute({product: product.id});
  }

  return (
    <>
      <Flex direction="column" height="100%">
        <Flex as="main" backgroundColor="white" direction="column" flex={1} height="100%">
          <Content height="100%" paddingX={{base: 0, sm: 4}}>
            <TenantHeader data-test-id="header" marginBottom={4} tenant={tenant} />
            <Box flex={1}>
              {highlight && (
                <Box
                  backgroundColor="primary.50"
                  color="primary.500"
                  fontSize={{base: "sm", sm: "md"}}
                  fontWeight="500"
                  marginTop={4}
                  paddingX={4}
                  paddingY={3}
                  roundedTop={{base: 0, sm: "lg"}}
                  textAlign={{base: "left", sm: "center"}}
                >
                  {highlight}
                </Box>
              )}
              <Box
                backgroundColor="gray.50"
                data-test-id="filters"
                marginBottom={{base: 5, sm: 10}}
                paddingX={4}
                paddingY={1}
                position="sticky"
                roundedBottom="lg"
                roundedTop={highlight ? "none" : "lg"}
                top={0}
                zIndex={3}
              >
                {element}
              </Box>
              <Box marginBottom={4} paddingX={0}>
                <Stack margin="auto" spacing={5} width="100%">
                  {Boolean(products.length) ? (
                    <Stack spacing={{base: 5, sm: 10}} width="100%">
                      {Boolean(featuredProducts.length) && (
                        <ProductsCarousel
                          paddingX={{base: 4, sm: 0}}
                          title={t("common.featured")}
                          zIndex={0}
                        >
                          {featuredProducts.map((product) => (
                            <ProductCard
                              key={product.id}
                              isRaised
                              layout="portrait"
                              minWidth={280}
                              product={product}
                              onClick={() => handleSelect(product)}
                            />
                          ))}
                        </ProductsCarousel>
                      )}
                      <CategoriesList
                        categories={productsByCategory}
                        flow={flow}
                        layout={layout}
                        selected={filters.state.category}
                        onCategoryClick={filters.actions.setCategory}
                        onProductClick={handleSelect}
                      />
                    </Stack>
                  ) : (
                    <Well data-test-id="empty">{t("products.empty")}</Well>
                  )}
                </Stack>
              </Box>
            </Box>
          </Content>
        </Flex>
      </Flex>
      {Boolean(items.length) && (
        <Flex
          as="nav"
          bottom={0}
          justifyContent="center"
          margin={{base: 0, sm: "auto"}}
          paddingBottom={4}
          paddingX={4}
          position="sticky"
          width="100%"
          zIndex={2}
        >
          <Box
            display="block"
            margin={{base: 0, sm: "auto"}}
            minWidth={{base: "100%", sm: 64}}
            rounded={4}
            width={{base: "100%", sm: "auto"}}
          >
            <SummaryButton items={items} onClick={handleOpenCart}>
              {t("products.review")}
            </SummaryButton>
          </Box>
        </Flex>
      )}
      {isCartOpen && (
        <CartSummaryDrawer
          fields={tenant.fields}
          hasCheckout={Boolean(tenant.phone)}
          items={items}
          onCheckout={checkout}
          onClose={closeCart}
          onDecrease={decrease}
          onIncrease={increase}
        />
      )}
      {Boolean(selected) && (
        <CartItemDrawer product={selected} onClose={handleCloseSelected} onSubmit={handleAdd} />
      )}
      <Onboarding />
    </>
  );
};

export default ProductsScreen;
